import { useCallback, useMemo } from "react";
import { Auth } from "shared/@auth/Auth";
import { useLogger } from "shared/@hooks/useLogger";

import { phonePattern } from "shared/@forms/PhoneInput";
import createNotification from "shared/@utils/createNotification";

import { IBOInfo } from "../../../useIBOInfo";

/**
 * Contacts is weird because while other tabs have separate data,
 * contact data comes with the top level IBOInfo object. When we commit changes,
 * the top level object gets refreshed which causes the entire page to update.
 * This is done by sharing state between the top level and here using and unstated
 * container instead of a normal hook, where state is separate.
 */
export function useContacts(iboNumber) {
  const { makeAuthedRequest } = Auth.useContainer();
  const { iboInfo, refreshIBOInfo } = IBOInfo.useContainer();
  const { logError } = useLogger({ prefix: "useContacts" });

  const isWaiting = iboInfo == null;

  const contacts = useMemo(() => {
    const { iboA, iboB } = iboInfo.primaryInfo;
    const list = [];

    if (iboA) {
      list.push({
        ...iboA,
        type: "IBO-A",
      });
    }

    if (iboB) {
      list.push({
        ...iboB,
        type: "IBO-B",
      });
    }

    return list;
  }, [iboInfo]);

  const commit = useCallback(
    async (data) => {
      const { iboNumber, iboA, iboB } = data;

      const json = JSON.stringify({
        iboNumber,
        iboA: convertContactToUpdateObject(iboA),
        iboB: convertContactToUpdateObject(iboB),
      });

      return makeAuthedRequest(`/v1/membership/ibo/${iboNumber}`, {
        method: "PUT",
        body: json,
      })
        .then(async (res) => {
          if (res.ok) {
            refreshIBOInfo();

            createNotification({
              type: "success",
              title: "Contacts Updated",
              message: "Contact was saved.",
              autoDismiss: true,
            });
          } else {
            let json = await res.json();
            console.log("useContacts /membership/ibo/ error json", json);

            createNotification({
              title: "Contacts Not Updated",
              message: json.message,
              type: "danger",
            });
          }
        })
        .catch((e) => {
          logError(e);
        });
    },
    [logError]
  );

  /**
   * Writes contact changes back to the database. Calling without passing
   * contact info will save with the existing contact info.
   */
  const update = useCallback(
    async (contact) => {
      const iboA =
        contact.type === "IBO-A"
          ? contact
          : contacts.find((c) => c.type === "IBO-A");

      const iboB =
        contact.type === "IBO-B"
          ? contact
          : contacts.find((c) => c.type === "IBO-B");

      const data = {
        iboNumber,
        iboA,
        iboB,
      };

      return commit(data);
    },
    [iboNumber, contacts]
  );

  const downloadBadge = useCallback(async (iboNumber, contactId) => {
    makeAuthedRequest(`/v1/membership/badge/${iboNumber}/${contactId}`)
      .then((res) => {
        return res.blob();
      })
      .then((blob) => {
        const href = window.URL.createObjectURL(blob);
        window.open(href);
      })
      .catch((err) => console.error(err));
  });

  const removeCoIBO = useCallback(async () => {
    return makeAuthedRequest(`/v1/membership/coibo/${iboNumber}`, {
      method: "DELETE",
    })
      .then(() => refreshIBOInfo())
      .then(() => {
        createNotification({
          type: "success",
          title: "IBO-B Removed",
          message: "Successfully removed IBO-B.",
          autoDismiss: true,
        });
      })
      .catch(logError);
  }, [iboNumber, logError]);

  return {
    contacts,
    refresh: refreshIBOInfo,
    update,
    downloadBadge,
    removeCoIBO,
    isWaiting,
  };
}

function convertContactToUpdateObject(data) {
  if (data) {
    const phone = data.phone
      ? data.phone.match(new RegExp(phonePattern))
      : null;

    return {
      contactPK: data.contactPK,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phoneNumber: phone ? [phone[1], phone[2], phone[3]] : null,
    };
  }

  return null;
}
